import { AppDispatch } from 'app/store';

import {
  navigatePage as navigatePageAction,
  pauseTour as pauseTourAction,
  setLoading as setLoadingAction
} from 'features/tour/tourSlice';

import { scrollToTop } from 'utils/scrollToTop';

import { TourState } from 'types/tour';

import { TOUR_SCROLL_TO_TOP_DELAY_MS } from 'constants/tour';

import { getNavigationPage } from './getNavigationPage';

type NavigateFn = (page: string) => void;
type ScrollToTargetFn = (selector: string) => Promise<boolean>;
type GetTourState = () => TourState;

const NAVIGATION_DELAY = 800;

export class TourNavigator {
  private navigate: NavigateFn;

  private scrollToTargetFn: ScrollToTargetFn;

  private dispatch: AppDispatch;

  private getState: GetTourState;

  constructor({
    navigate,
    scrollToTarget,
    dispatch,
    getState
  }: {
    navigate: NavigateFn;
    scrollToTarget: ScrollToTargetFn;
    dispatch: AppDispatch;
    getState: GetTourState;
  }) {
    this.navigate = navigate;
    this.scrollToTargetFn = scrollToTarget;
    this.dispatch = dispatch;
    this.getState = getState;
  }

  async goToPage(page: string, stepIndex: number) {
    this.dispatch(setLoadingAction(true));
    this.dispatch(pauseTourAction());

    const state = this.getState();
    const targetPage = getNavigationPage(
      page,
      state.jobId,
      state.applicationId
    );

    this.navigate(targetPage);

    await this.waitForNavigation();

    scrollToTop();
    await new Promise((resolve) =>
      setTimeout(resolve, TOUR_SCROLL_TO_TOP_DELAY_MS)
    );

    try {
      await this.scrollToStepTarget(stepIndex);
      this.dispatch(navigatePageAction({ page, stepIndex }));
    } finally {
      this.dispatch(setLoadingAction(false));
    }
  }

  async scrollToTarget(selector: string | undefined) {
    if (!selector) {
      return;
    }

    await this.scrollToTargetFn(selector);
  }

  private async scrollToStepTarget(stepIndex: number) {
    const state = this.getState();
    const step = state.steps[stepIndex];

    if (step?.target) {
      await this.scrollToTargetFn(step.target as string);
    }
  }

  private waitForNavigation() {
    return new Promise((resolve) => setTimeout(resolve, NAVIGATION_DELAY));
  }
}
